import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchParam } from "react-use";
import Header from "../../components/layouts/Header";
import LoadingSpinner from "../../components/loaders/LoadingSpinner";
import {
  ENVIRONMENT,
  STRIPE_PRICING_TABLE_ID,
  STRIPE_PRICING_TABLE_PUBLISHABLE_KEY,
} from "../../config";
import {
  ROUTE_ARTICLE_CREATE /*ROUTE_ROOT*/,
  ROUTE_PLANS,
  ROUTE_ROOT,
} from "../../constants/routes";
import { useAppSelector } from "../../redux/hooks";
import { useGetSubscriptionQuery } from "../../redux/services/subscription";
import { useGetUserQuery } from "../../redux/services/user";
import { RootState } from "../../redux/store";
import { createCustomerSession } from "../../stripe";

declare global {
  interface Window {
    Rewardful: {
      referral: string;
    };
  }
  namespace JSX {
    interface IntrinsicElements {
      "stripe-pricing-table": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
    }
  }
}

// TODO: on verify redirect no retry - use query param
// https://dashboard.stripe.com/pricing-tables
const Plans = () => {
  const navigate = useNavigate();
  const subscribed = useSearchParam("subscribed");
  const authToken = useAppSelector((state: RootState) => state.auth.token);
  const [clientSecret, setClientSecret] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // User
  const { data: user } = useGetUserQuery();
  const selectedAccount = user?.accounts[0] ?? null;
  const accountId = selectedAccount?.accountId ?? null;
  const stripeCustomerId = selectedAccount?.stripeCustomerId ?? null;

  // Subscription
  const {
    data: subscription,
    isSuccess,
    refetch,
  } = useGetSubscriptionQuery(
    {
      accountId,
    },
    { skip: !accountId }
  );

  const getClientSecret = useCallback(async () => {
    if (!authToken) return;

    await createCustomerSession(stripeCustomerId, authToken).then((res) => {
      if (res?.client_secret) {
        setClientSecret(res.client_secret);
      }
    });
  }, [authToken, stripeCustomerId]);

  useEffect(() => {
    if (!isSuccess) return;
    if (subscription) {
      if (subscription?.status === "canceled") {
        navigate(ROUTE_ROOT);
      } else {
        navigate(ROUTE_ARTICLE_CREATE);
      }
      return;
    }

    if (!subscribed && !clientSecret && stripeCustomerId && authToken) {
      getClientSecret();
    }

    if (subscribed) {
      // Long polling subscription api
      setLoading(true);
      const timeoutId = setTimeout(() => {
        if (retryCount < 3) {
          refetch();
          setRetryCount((prev) => prev + 1);
        } else {
          setLoading(false);
        }
      }, 5000);

      if (retryCount === 3 && !clientSecret) {
        getClientSecret();
      }
      // Clean up the timeout on component unmount or effect re-run
      return () => clearTimeout(timeoutId);
    }
  }, [
    isSuccess,
    subscribed,
    subscription,
    clientSecret,
    stripeCustomerId,
    authToken,
    retryCount,
    getClientSecret,
    navigate,
    refetch,
  ]);

  // Add a client-reference-id to stripe-pricing-table for Rewardful-Stripe integration
  const pricingTableElement = document.querySelector("stripe-pricing-table");
  useEffect(() => {
    if (pricingTableElement && ENVIRONMENT === "prod") {
      const referralId = window.Rewardful ? window.Rewardful.referral : null;
      if (referralId) {
        pricingTableElement.setAttribute("client-reference-id", referralId);
      }
    }
  }, [pricingTableElement]);

  return (
    <div className="relative">
      <Header onLogoClick={() => navigate(ROUTE_PLANS)} />
      {loading && (
        <div className="flex flex-col gap-2 items-center justify-center mt-24">
          <LoadingSpinner size="32px" />
        </div>
      )}
      {!loading && (
        <div>
          <div className="text-base md:text-xl text-center my-8 px-4">
            Choose the plan that best fits your team and goals.{" "}
            <b>Free 7-day trial</b>.
          </div>
          <stripe-pricing-table
            pricing-table-id={STRIPE_PRICING_TABLE_ID}
            publishable-key={STRIPE_PRICING_TABLE_PUBLISHABLE_KEY}
            customer-session-client-secret={clientSecret}
          ></stripe-pricing-table>
        </div>
      )}
    </div>
  );
};

export default Plans;
